import i18n from "@i18n";
import biomarkersSeo from "src/components/pdp/assets/biomarkersSeo.webp";
import metabolicMenSeo from "src/components/pdp/assets/metabolicMenSeo.webp";
import metabolicWomenSeo from "src/components/pdp/assets/metabolicWomenSeo.webp";
import nadSeo from "src/components/pdp/assets/nadSeo.webp";
import oxytocinSeo from "src/components/pdp/assets/oxytocinSeo.webp";
import sermorelinSeo from "src/components/pdp/assets/sermorelinSeo.webp";
import vitalitySeo from "src/components/pdp/assets/vitalitySeo.webp";
import nadEligibleHero from "src/components/shop/images/nadEligibleHero.webp";
import oxytocinEligibleHero from "src/components/shop/images/oxytocinEligibleHero.webp";
import sermorelinEligibleHero from "src/components/shop/images/sermorelinEligibleHero.webp";
import {
  ADDON_SKU,
  ADDON_SKU_TO_PRODUCT_NAME,
  isStateEligibleForAddon,
} from "./addonEligibility";
import { getEligibleMedProducts, MED_SKU } from "./medEligibility";
import getStore, { CoreReduxState } from "./redux/store";
import { CheckoutOrderEntry } from "./redux/slices/checkoutOrders";

export enum ShopEligibleCountries {
  US = "US",
}

export enum ShopProduct {
  OTC_BIOMARKERS = "OTC_BIOMARKERS",
  OTC_METABOLIC_MEN = "OTC_METABOLIC_MEN",
  OTC_METABOLIC_WOMEN = "OTC_METABOLIC_WOMEN",
  OTC_VITALITY_SUPPLEMENT_SUB = "OTC_VITALITY_SUPPLEMENT_SUB",
  PEPTIDE_SERMORELIN = "PEPTIDE_SERMORELIN",
  RX_NAD_PLUS = "RX_NAD_PLUS",
  PEPTIDE_OXYTOCIN_ODT = "PEPTIDE_OXYTOCIN_ODT",
}

export const ACCOUNT_FIELD = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  DATE_OF_BIRTH: "dateOfBirth",
  BIOLOGICAL_SEX: "biologicalSex",
  PHONE_NUMBER: "phoneNumber",
  SMS_CONSENT: "smsConsent",
} as const;

export type AccountField = (typeof ACCOUNT_FIELD)[keyof typeof ACCOUNT_FIELD];

export type ShopHeroImage = {
  src: string;
  width: number;
  height: number;
};

/**
 * Indexable shop PDP meta. Setting `seo` requires both the URL slug and the
 * OG image, so an entry cannot opt into indexing without a canonical path.
 */
export interface ShopPdpSeo {
  slug: string;
  ogImage: string;
}

/**
 * Name, date of birth and phone number are mandatory in order to create a
 * patient entry (see `createPatientData`), so every shop product collects them.
 */
const SUPPLEMENT_REQUIRED_ACCOUNT_FIELDS: AccountField[] = [
  ACCOUNT_FIELD.FIRST_NAME,
  ACCOUNT_FIELD.LAST_NAME,
  ACCOUNT_FIELD.DATE_OF_BIRTH,
  ACCOUNT_FIELD.PHONE_NUMBER,
  ACCOUNT_FIELD.SMS_CONSENT,
];

/**
 * Biological sex is not needed to create a patient entry, but biomarker lab
 * orders are rejected without a male/female sex, and the medical team requires
 * it for medication products (MGWL-678).
 */
const CLINICAL_REQUIRED_ACCOUNT_FIELDS: AccountField[] = [
  ACCOUNT_FIELD.FIRST_NAME,
  ACCOUNT_FIELD.LAST_NAME,
  ACCOUNT_FIELD.DATE_OF_BIRTH,
  ACCOUNT_FIELD.BIOLOGICAL_SEX,
  ACCOUNT_FIELD.PHONE_NUMBER,
  ACCOUNT_FIELD.SMS_CONSENT,
];

interface BaseShopEntry {
  cartKey: string;
  requiredAccountFields: AccountField[];
  /**
   * When true, an active subscription for this product locks the saved
   * shipping address on checkout (it cannot be edited). Set this for
   * recurring physical-shipment products. See
   * `useSavedPaymentAndShippingEditability`.
   */
  locksSavedShippingAddress?: boolean;
  /**
   * When true, the FSA/HSA reimbursement footnote is shown for this product at
   * checkout (i.e. the product qualifies for FSA/HSA reimbursement).
   */
  eligibleForFsaHsa?: boolean;
  /**
   * When true, the product is dispensed by a pharmacy, so its shipping address
   * must satisfy the same constraints as a med plan: no PO boxes, and address
   * lines within `MAX_ADDRESS_LINE_LENGTH`.
   */
  shipsMedication?: boolean;
  /**
   * Survey: allow continuing with low BMI (no hard-stop). Product-specific —
   * gate with `isShopLayer()` so a stale addonsCart cannot apply this outside
   * shop flows.
   */
  allowLowBmiInSurvey?: boolean;
  /**
   * Survey: on basicWeight, hide subtext and success message and pin Next to
   * the bottom on mobile.
   */
  basicWeightMinimalSurveyUi?: boolean;
  /**
   * Survey: answering yes to eating disorder uses the confirm/waiver flow.
   * Product-specific — gate with `isShopLayer()` so a stale addonsCart cannot
   * apply this outside shop flows.
   */
  eatingDisorderConfirmFlow?: boolean;
  /**
   * Survey: answering yes to pregnant routes to the ineligibility interstitial
   * instead of inline error / liability waiver. Product-specific — gate with
   * `isShopLayer()` so a stale addonsCart cannot apply this outside shop flows.
   */
  pregnantIneligibilityFlow?: boolean;
  /**
   * Prescription/peptide product rather than an OTC supplement. Drives the
   * med-specific branches of the cancellation survey (administration issues,
   * telehealth competitors as alternative sources).
   */
  isMedication?: boolean;
  /**
   * i18next context suffix for product-specific copy. Absent means the
   * generic Noom copy is used.
   */
  i18nContext?: string;
  /**
   * When true, buying this product sends the user into the clinical intake, and
   * the pre-intake poll waits for its treatment. Set this for prescription
   * products such as peptides.
   */
  requiresIntake?: boolean;
  /**
   * i18n key of the product's general display name (e.g. "NAD+ for Energy").
   * Read it through `getShopProductDisplayName`.
   */
  displayNameKey?: string;
  /**
   * Hero image of the eligibility-confirmed screen, also shown behind the
   * logged-out email capture that overlays it.
   */
  eligibilityHero?: ShopHeroImage;
  /**
   * When set, the shop PDP emits indexable product meta (title/description in
   * `pdp:seo:<slug>`). Omit for unlaunched or noindex PDPs.
   */
  seo?: ShopPdpSeo;
  /** Whole health purchase intent pixels fired from shop checkout. */
  purchaseIntentPixels: ShopPurchaseIntentPixels;
}

export interface ShopPurchaseIntentPixels {
  /** Meta custom event fired on the med pixel after WhiteHeather. */
  metaEventName: string;
}

export interface AddonShopEntry extends BaseShopEntry {
  type: "addon";
  addonSku: ADDON_SKU;
}

export interface MedPlanShopEntry extends BaseShopEntry {
  type: "medPlan";
  medSku: MED_SKU;
}

export type ShopEntry = AddonShopEntry | MedPlanShopEntry;

export const SHOP_REGISTRY: Partial<Record<ShopProduct, ShopEntry>> = {
  [ShopProduct.OTC_BIOMARKERS]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.OTC_BIOMARKERS],
    addonSku: ADDON_SKU.OTC_BIOMARKERS,
    requiredAccountFields: CLINICAL_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Bluebell" },
    eligibleForFsaHsa: true,
    seo: { slug: "biomarkers-test-kit", ogImage: biomarkersSeo },
  },
  [ShopProduct.OTC_METABOLIC_MEN]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.OTC_METABOLIC_MEN],
    addonSku: ADDON_SKU.OTC_METABOLIC_MEN,
    requiredAccountFields: SUPPLEMENT_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Marigold" },
    locksSavedShippingAddress: true,
    i18nContext: "metabolicMen",
    seo: { slug: "metabolic-for-men", ogImage: metabolicMenSeo },
  },
  [ShopProduct.OTC_METABOLIC_WOMEN]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.OTC_METABOLIC_WOMEN],
    addonSku: ADDON_SKU.OTC_METABOLIC_WOMEN,
    requiredAccountFields: SUPPLEMENT_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Marigold" },
    locksSavedShippingAddress: true,
    i18nContext: "metabolicWomen",
    seo: { slug: "metabolic-for-women", ogImage: metabolicWomenSeo },
  },
  [ShopProduct.OTC_VITALITY_SUPPLEMENT_SUB]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.OTC_VITALITY_SUPPLEMENT_SUB],
    addonSku: ADDON_SKU.OTC_VITALITY_SUPPLEMENT_SUB,
    requiredAccountFields: SUPPLEMENT_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Violet" },
    locksSavedShippingAddress: true,
    i18nContext: "vitalitySupplement",
    seo: { slug: "vitality", ogImage: vitalitySeo },
  },
  [ShopProduct.PEPTIDE_SERMORELIN]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.PEPTIDE_SERMORELIN],
    addonSku: ADDON_SKU.PEPTIDE_SERMORELIN,
    requiredAccountFields: CLINICAL_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Snapdragon" },
    locksSavedShippingAddress: true,
    shipsMedication: true,
    allowLowBmiInSurvey: true,
    basicWeightMinimalSurveyUi: true,
    eatingDisorderConfirmFlow: true,
    pregnantIneligibilityFlow: true,
    isMedication: true,
    i18nContext: "sermorelin",
    requiresIntake: true,
    displayNameKey: "shop:productNames:PEPTIDE_SERMORELIN",
    eligibilityHero: {
      src: sermorelinEligibleHero,
      width: 239,
      height: 191,
    },
    seo: { slug: "sermorelin", ogImage: sermorelinSeo },
  },
  [ShopProduct.RX_NAD_PLUS]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.RX_NAD_PLUS],
    addonSku: ADDON_SKU.RX_NAD_PLUS,
    requiredAccountFields: CLINICAL_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Narcissus" },
    locksSavedShippingAddress: true,
    shipsMedication: true,
    // Weight is collected for the patient record but is not an NAD+ eligibility
    // criterion, so there is no BMI hard-stop in this flow.
    allowLowBmiInSurvey: true,
    basicWeightMinimalSurveyUi: true,
    isMedication: true,
    i18nContext: "nadPlus",
    requiresIntake: true,
    pregnantIneligibilityFlow: true,
    displayNameKey: "shop:productNames:RX_NAD_PLUS",
    eligibilityHero: {
      src: nadEligibleHero,
      width: 332,
      height: 221,
    },
    seo: { slug: "nad", ogImage: nadSeo },
  },
  [ShopProduct.PEPTIDE_OXYTOCIN_ODT]: {
    type: "addon",
    cartKey: ADDON_SKU_TO_PRODUCT_NAME[ADDON_SKU.PEPTIDE_OXYTOCIN_ODT],
    addonSku: ADDON_SKU.PEPTIDE_OXYTOCIN_ODT,
    requiredAccountFields: CLINICAL_REQUIRED_ACCOUNT_FIELDS,
    purchaseIntentPixels: { metaEventName: "Oleander" },
    locksSavedShippingAddress: true,
    shipsMedication: true,
    // Weight is collected for the patient record but is not an Oxytocin
    // eligibility criterion, so there is no BMI hard-stop in this flow.
    allowLowBmiInSurvey: true,
    basicWeightMinimalSurveyUi: true,
    isMedication: true,
    i18nContext: "oxytocin",
    requiresIntake: true,
    pregnantIneligibilityFlow: true,
    displayNameKey: "shop:productNames:PEPTIDE_OXYTOCIN_ODT",
    eligibilityHero: {
      src: oxytocinEligibleHero,
      width: 332,
      height: 221,
    },
    seo: { slug: "oxytocin", ogImage: oxytocinSeo },
  },
};

export function getShopProductDisplayName(
  product: ShopProduct
): string | undefined {
  const key = SHOP_REGISTRY[product]?.displayNameKey;
  return key ? i18n.t(key) : undefined;
}

/** Shop PDPs with indexable SEO meta (`seo` on SHOP_REGISTRY). */
export function getIndexableShopPdpSlugs(): string[] {
  return (Object.values(SHOP_REGISTRY) as ShopEntry[])
    .map((entry) => entry.seo?.slug)
    .filter((slug): slug is string => !!slug);
}

export function getShopPdpSeoForProduct(
  product: ShopProduct
): ShopPdpSeo | undefined {
  return SHOP_REGISTRY[product]?.seo;
}

/**
 * Medication supply length shown in the shop order summary, mirroring
 * `getMedicationSupplyFromBillingCycle` on clinical checkout (trial = 4 weeks,
 * refill = 12 weeks). This is product/supply copy — not billing-interval
 * duration.
 */
export function getShopSupplyLengthInWeeks(
  product: ShopProduct,
  isTrial: boolean
): number | undefined {
  if (
    product === ShopProduct.PEPTIDE_SERMORELIN ||
    product === ShopProduct.RX_NAD_PLUS ||
    product === ShopProduct.PEPTIDE_OXYTOCIN_ODT
  ) {
    return isTrial ? 4 : 12;
  }
  return undefined;
}

export function getShopProductByCartKey(
  cartKey: string
): ShopProduct | undefined {
  const entries = Object.entries(SHOP_REGISTRY) as [ShopProduct, ShopEntry][];
  const match = entries.find(([, entry]) => entry.cartKey === cartKey);
  return match?.[0];
}

export function getShopEntryByProductName(
  productName?: string
): ShopEntry | undefined {
  const product = productName
    ? getShopProductByCartKey(productName)
    : undefined;
  return product ? SHOP_REGISTRY[product] : undefined;
}

/**
 * Returns the SHOP_REGISTRY entry for the subscription the user currently
 * owns (read from `serverContext.product_name`). Distinct from
 * `getActiveShopOrderEntry` / `getActiveShopProduct`, which describe the
 * product in the checkout cart being purchased right now.
 */
export function getSubscribedShopEntry(
  state: CoreReduxState = getStore().getState()
): ShopEntry | undefined {
  return getShopEntryByProductName(state.serverContext.product_name);
}

/**
 * Whether the product is pharmacy-dispensed, and so needs the same strict
 * shipping-address handling as a med plan. Driven by `shipsMedication` in
 * SHOP_REGISTRY.
 */
export function shopProductShipsMedication(product: ShopProduct): boolean {
  return !!SHOP_REGISTRY[product]?.shipsMedication;
}

/**
 * Whether a product (identified by its product name, which equals the registry
 * `cartKey`) locks the saved shipping address while it has an active
 * subscription. Driven by `locksSavedShippingAddress` in SHOP_REGISTRY so new
 * shippable products opt in by registry config alone.
 */
export function productNameLocksSavedShippingAddress(
  productName: string
): boolean {
  const product = getShopProductByCartKey(productName);
  const entry = product ? SHOP_REGISTRY[product] : undefined;
  return !!entry?.locksSavedShippingAddress;
}

/**
 * Whether buying this product requires a clinical intake. Driven by
 * `requiresIntake` in SHOP_REGISTRY so new prescription products opt in by
 * registry config alone.
 */
export function shopProductRequiresIntake(product: ShopProduct): boolean {
  return !!SHOP_REGISTRY[product]?.requiresIntake;
}

/**
 * Whether the product's checkout collects biological sex. Driven by
 * `requiredAccountFields` in SHOP_REGISTRY.
 */
export function shopProductCollectsBiologicalSex(
  product: ShopProduct
): boolean {
  return !!SHOP_REGISTRY[product]?.requiredAccountFields.includes(
    ACCOUNT_FIELD.BIOLOGICAL_SEX
  );
}

/**
 * Same as `shopProductRequiresIntake`, for callers holding a product name
 * rather than a `ShopProduct` — the name equals the registry `cartKey`.
 */
export function productNameRequiresIntake(productName: string): boolean {
  const product = getShopProductByCartKey(productName);
  return !!product && shopProductRequiresIntake(product);
}

/**
 * Whether the FSA/HSA reimbursement footnote should be shown for a shop order
 * entry. Driven by `eligibleForFsaHsa` in SHOP_REGISTRY.
 */
export function isShopEntryEligibleForFsaHsa(
  entry: CheckoutOrderEntry
): boolean {
  const product = getShopProductByCartKey(entry.productName);
  const registryEntry = product ? SHOP_REGISTRY[product] : undefined;
  return !!registryEntry?.eligibleForFsaHsa;
}

export function getActiveShopProduct(
  state: CoreReduxState = getStore().getState()
): ShopProduct | undefined {
  const cart = state.checkoutOrders.addonsCart;
  if (!cart) return undefined;

  for (const cartKey of cart) {
    const product = getShopProductByCartKey(cartKey);
    if (product) return product;
  }
  return undefined;
}

export function getActiveShopEntry(
  state: CoreReduxState = getStore().getState()
): ShopEntry | undefined {
  const product = getActiveShopProduct(state);
  return product ? SHOP_REGISTRY[product] : undefined;
}

/**
 * The entry `ShopCheckout` renders. Mirrors `cartEntries[0]` from
 * `selectAddonsCart` (skips missing keys via `.find(Boolean)`).
 *
 * If it drifts from the rendered entry, the consent gate disagrees with the UI.
 */
export function getActiveShopOrderEntry(
  state: CoreReduxState = getStore().getState()
): CheckoutOrderEntry | undefined {
  const { addonsCart, addons } = state.checkoutOrders;
  if (!addonsCart || !addons) return undefined;

  return addonsCart.map((cartKey) => addons[cartKey]).find(Boolean);
}

/**
 * Whether a shop order entry is a recurring subscription. Checks the purchase
 * type positively so an absent or unknown type is not treated as a subscription.
 */
export function isShopSubscriptionEntry(entry: CheckoutOrderEntry): boolean {
  return entry.purchaseType === "SUBSCRIPTION";
}

/**
 * Whether a shop entry has both an initial (intro) cycle and a recurring cycle
 * with invoices — i.e. it is a trial/intro product like sermorelin.
 */
export function hasShopInitialCycle(entry: CheckoutOrderEntry): boolean {
  return (
    !!entry.initialBillingCycle?.invoice &&
    !!entry.recurringBillingCycle?.invoice
  );
}

export function isShopProductInCart(
  product: ShopProduct,
  state: CoreReduxState = getStore().getState()
): boolean {
  const entry = SHOP_REGISTRY[product];
  if (!entry) return false;
  return Boolean(state.checkoutOrders.addonsCart?.includes(entry.cartKey));
}

/**
 * `shopProduct` defaults to the cart so checkout callers need not pass it.
 * Callers outside checkout (e.g. the change-address page, which has no cart)
 * pass the product resolved from the shipment instead.
 */
export function validateShopProductShippingLocationEligibility(
  country: string,
  zipState: string,
  shopProduct: ShopProduct | undefined = getActiveShopProduct()
): "country" | "postcode" | null {
  if (!(country in ShopEligibleCountries)) {
    return "country";
  }

  if (!shopProduct) return null;

  const entry = SHOP_REGISTRY[shopProduct];
  if (!entry) return null;

  if (entry.type === "addon") {
    if (!isStateEligibleForAddon(entry.addonSku, zipState)) {
      return "postcode";
    }
  } else if (entry.type === "medPlan") {
    const eligible = getEligibleMedProducts({
      stateOverwrite: zipState,
      checkBMI: true,
      checkHealthRisks: true,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
    });
    if (!eligible.includes(entry.medSku)) {
      return "postcode";
    }
  }

  return null;
}
